// Shared presentation helpers for the trust prototype pages.

import { Explain } from './Explain';

// Theme-aware relation colors: the values live in app.css (brighter in dark
// so they hold up as text ink); .relation-pill pairs them with var(--wv-bg).
export const RELATION_COLORS = {
  supports: 'var(--wv-rel-supports)',
  contradicts: 'var(--wv-rel-contradicts)',
  // a bounded exception is neither side — its own ink, not a shade of red
  qualifies: 'var(--wv-rel-qualifies)',
  related_to: 'var(--wv-rel-related)',
  references: 'var(--wv-rel-references)',
  derived_from: 'var(--wv-rel-derived)',
  defines: 'var(--wv-rel-defines)',
  contains: 'var(--wv-rel-contains)',
};

export const relationColor = (name) => RELATION_COLORS[name] || 'var(--wv-rel-fallback)';

// Relation picker ordering (owner 2026-07-06: filtering dozens of relation
// types is tedious). The epistemic core is what a person almost always means,
// so it pins to the top of every picker; everything else follows
// alphabetically. User-defined types are still first-class — they just sort
// after the seeds.
export const RELATION_PRIORITY = [
  'supports',
  'contradicts',
  'qualifies',
  'related_to',
  'restates',
  'references',
  'tagged',
  'derived_from',
  'defines',
  'contains',
  'is_subcategory_of',
];

export function sortRelations(relations) {
  const rank = (name) => {
    const i = RELATION_PRIORITY.indexOf(name);
    return i === -1 ? RELATION_PRIORITY.length : i;
  };
  return [...relations].sort(
    (a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name)
  );
}

// Continuous diverging scale: red (-1) → gray (0) → green (+1)
const lerp = (a, b, t) => Math.round(a + (b - a) * t);
const mix = (c1, c2, t) =>
  `rgb(${lerp(c1[0], c2[0], t)}, ${lerp(c1[1], c2[1], t)}, ${lerp(c1[2], c2[2], t)})`;
const RED = [185, 28, 28];
const GRAY = [156, 163, 175];
const GREEN = [21, 128, 61];

export const scoreScale = (s) => {
  const v = Math.max(-1, Math.min(1, s));
  return v >= 0 ? mix(GRAY, GREEN, v) : mix(GRAY, RED, -v);
};

// Text variant: keeps neutral readable instead of mid-gray-on-white
export const scoreColor = (s) =>
  s > 0.05 ? 'var(--wv-pos)' : s < -0.05 ? 'var(--wv-neg)' : 'var(--wv-muted)';

export const fmtScore = (s) => (s > 0 ? '+' : '') + s.toFixed(2);

// Diverging bar centered on zero: trust grows right (green), distrust left (red).
export function TrustBar({ score, width = 160, height = 8 }) {
  const half = width / 2;
  const mag = Math.min(Math.abs(score), 1) * half;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <rect x={0} y={0} width={width} height={height} rx={height / 2} fill="var(--wv-border)" />
      <rect
        x={score >= 0 ? half : half - mag}
        y={0}
        width={Math.max(mag, 0.01)}
        height={height}
        rx={height / 2}
        fill={scoreScale(score)}
      />
      <rect x={half - 0.75} y={-1} width={1.5} height={height + 2} fill="var(--wv-quiet)" />
    </svg>
  );
}

// The spread tick: a miniature band showing the RANGE of viewer-blended scores
// across a set of neighbouring objects, for a row that is collapsed. It answers
// "is what's under here agreed or split?" without opening it.
//
// Moved here from Argument.js 2026-07-28 (it was private to the Walk's folds)
// when the relation tree's collapsed rows needed the same readout. Two surfaces
// wanting the identical mark is the project's own signal to share the component
// rather than grow a second copy that drifts — the EdgeSentence pattern.
//
// `spread` is {min, max} of already-blended scores, or null. Callers pass null
// for an unscored set (a question carries no truth value): absence renders as
// absence, never as a tick sitting at 0.
export function SpreadTick({ spread, width = 64, height = 10 }) {
  if (!spread) return null;
  const x = (v) => ((Math.max(-1, Math.min(1, v)) + 1) / 2) * width;
  const bandX = x(spread.min);
  const bandW = Math.max(x(spread.max) - bandX, 2);
  return (
    <svg width={width} height={height} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <rect x={0} y={height / 2 - 1.5} width={width} height={3} rx={1.5} fill="var(--wv-border)" />
      <rect x={width / 2 - 0.5} y={1} width={1} height={height - 2} fill="var(--wv-border-strong)" />
      <rect
        x={bandX} y={height / 2 - 3.5} width={bandW} height={7} rx={3.5}
        fill={scoreScale((spread.min + spread.max) / 2)} opacity={0.6}
      />
    </svg>
  );
}

// Trust band: the headline trust readout after the UX session decision
// (2026-07-03) that an unweighted mean must not be a headline. The BAND spans
// the lens scores' range (its width is the lens spread — how much perspectives
// disagree); the MARKER is the viewer's own slider blend, explicitly a
// viewpoint. Callers must render no_signal as absence, never as this at 0.
export function TrustBand({ scores, blend, width = 260, height = 16 }) {
  const values = Object.values(scores);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const x = (v) => ((Math.max(-1, Math.min(1, v)) + 1) / 2) * width;
  const bandX = x(min);
  const bandW = Math.max(x(max) - bandX, 2);
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      {/* the blend's meter family (2026-07-31), row-sized: soft track, zero
          line, warn spread, dot marker — no printed scale at this size */}
      <rect x={0} y={height / 2 - 2.5} width={width} height={5} rx={2.5} fill="var(--wv-surface2)" />
      <rect x={width / 2 - 0.5} y={1} width={1} height={height - 2} fill="var(--wv-border-strong)" />
      <rect
        x={bandX}
        y={height / 2 - 4.5}
        width={bandW}
        height={9}
        rx={4.5}
        style={{ fill: 'color-mix(in srgb, var(--wv-warn) 22%, transparent)' }}
      />
      <circle cx={x(blend)} cy={height / 2} r={5} fill="var(--wv-text)" stroke="var(--wv-surface)" strokeWidth={2.5}>
        <title>{`your blend: ${fmtScore(blend)}`}</title>
      </circle>
    </svg>
  );
}

// Provenance badge: the human-verified vs AI-curated barrier, surfaced per item.
// Only humans vote, so an activated (voted) edge is human-verified; an unvoted edge
// is AI-scaffolded-or-unverified and inert in trust math. See DECISIONS.md 2026-06-19.
export function ProvenanceBadge({ activated, upVotes = 0, downVotes = 0 }) {
  if (activated) {
    const contested = upVotes > 0 && downVotes > 0;
    const total = upVotes + downVotes;
    // REJECTED IS ITS OWN STATE (2026-08-02, found by a web-Claude research
    // session driving the Walk). `activated` answers "did a human judge this",
    // which is the right question for the trust math — an edge everybody
    // downvoted is still a participant, carrying a negative vote_score. It is
    // the WRONG question for a badge: an edge with one disagree and zero agrees
    // was rendering as a green ✓ verified, so the only signal the system has
    // read as its own opposite. `contested` already carved out the mixed case;
    // the all-against case fell through it because it needs upVotes > 0.
    // Activation is unchanged — this is a render split, not a boundary move.
    const rejected = upVotes === 0 && downVotes > 0;
    if (rejected) {
      return (
        <Explain
          title="Rejected (activated)"
          text={`${total} human vote${total === 1 ? '' : 's'} on this connection, and every one of them disagreed. It is activated — it participates in trust scores — but it participates AGAINST: a rejected connection pulls its target's score down rather than up. Voted-on is not the same as endorsed.`}
        >
          <span
            className="prov-badge prov-rejected"
            title={`Rejected — ${total} vote${total === 1 ? '' : 's'}, all against`}
          >
            ✕ rejected
          </span>
        </Explain>
      );
    }
    return (
      <Explain
        title="Verified (activated)"
        text={`${total} human vote${total === 1 ? '' : 's'} on this connection${contested ? ' — and they disagree' : ''}. Voting is what activates an edge: only activated edges participate in trust scores, and since only humans vote, every activated edge was human-verified.`}
      >
        <span
          className="prov-badge prov-verified"
          title={`Human-verified — ${total} vote${total === 1 ? '' : 's'}${contested ? ', contested' : ''}`}
        >
          ✓ verified{contested ? ' · contested' : ''}
        </span>
      </Explain>
    );
  }
  return (
    <Explain
      title="AI-suggested (inert)"
      text="No human has voted on this connection yet, so it is inert — it contributes nothing to any trust score. It may have been drawn by AI or by a person; until a vote activates it, the system treats both as unverified."
    >
      <span
        className="prov-badge prov-unverified"
        title="Unverified — no human votes yet. Whether a person or AI drew it, the edge stays inert until a human votes."
      >
        ◌ unverified
      </span>
    </Explain>
  );
}

// One chip per lens with its score; stub lenses are visibly flagged so
// nobody mistakes prototype numbers for real trust.
const LENS_STATUS_NOTE = {
  naive:
    ' (This lens is deliberately naive — a placeholder formula that validates the UI, not a real trust algorithm.)',
  stub: ' (This lens is a stub — treat its numbers as a sketch, not a signal.)',
};

export function LensChips({ lenses, scores, size = 'normal' }) {
  const fontSize = size === 'small' ? '11px' : '12px';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {lenses.map((lens) => {
        const s = scores[lens.id] ?? 0;
        return (
          <Explain
            key={lens.id}
            title={`${lens.label} lens`}
            text={`${lens.description}${LENS_STATUS_NOTE[lens.status] || ''}`}
            graphKey={`lens:${lens.id}`}
          >
            <span className="lens-chip" title={lens.description} style={{ fontSize }}>
              {lens.label}
              <strong style={{ color: scoreColor(s) }}>{fmtScore(s)}</strong>
              {lens.status === 'stub' && <span className="stub-badge">stub</span>}
            </span>
          </Explain>
        );
      })}
    </div>
  );
}
